import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import useSWRMutation from "swr/mutation";
import { useSWRConfig } from "swr";
import { BellRingIcon, ChevronDownIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { RowCard } from "@/components/list/row-card";
import { RowIndex } from "@/components/list/row-index";
import { Section } from "@/components/section";
import { useApp } from "@/hooks/apps";
import { useToast } from "@/hooks/use-toast";
import { postFetcher, putFetcher, useAuthStore, type HttpError } from "@/lib/swr";
import { INBOX_COUNT_KEY, INBOX_KEY } from "@/hooks/inbox";
import { engagementDetailPath } from "@/lib/paths";
import { toastError } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { STATUS_ICON, STATUS_LABEL, StatusIcon } from "./completion-progress";
import type { Engagement, IssueType, PersonalStatus, Prd } from "@/types/domain";

type CompletionResult = {
  id: string;
  myCompletedAt: string | null;
  myStartedAt?: string | null;
};

export const personalStatusOf = (e: Pick<Engagement, "myCompletedAt" | "myStartedAt">): PersonalStatus =>
  e.myCompletedAt ? "done" : e.myStartedAt ? "in_progress" : "open";

type OpenTodo = { engagementId: string; shortId: string | null; title: string };
type OpenTodos = { todos: OpenTodo[]; hiddenCount: number };

// 服务端拒绝完成 bug 时带回的未完成子 todo(WED-183):可见的给清单,看不到的只有计数;其它错误返回 null
const openTodosOf = (error: unknown): OpenTodos | null => {
  const { status, data } = (error ?? {}) as HttpError;
  const body = data as { code?: string; openTodos?: OpenTodo[]; hiddenCount?: number } | undefined;
  return status === 409 && body?.code === "OPEN_TODOS"
    ? { todos: body.openTodos ?? [], hiddenCount: body.hiddenCount ?? 0 }
    : null;
};

// Portal content still bubbles React events to the row (inbox RowCard
// navigates on click), so the dialog swallows them.
const OpenTodosDialog = ({ todos, hiddenCount, onCancel, onConfirm }: OpenTodos & {
  onCancel: () => void;
  onConfirm: () => void;
}) => (
  <div className="contents" onClick={(event) => event.stopPropagation()}>
    <AlertDialog open onOpenChange={(open) => { if (!open) onCancel(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>还有 {todos.length + hiddenCount} 条 todo 未完成</AlertDialogTitle>
          <AlertDialogDescription>
            这个 bug 下的 todo 还没完成，确认仍要标记完成吗？todo 会继续留在负责人的待办里。
          </AlertDialogDescription>
        </AlertDialogHeader>
        <ul className="max-h-48 space-y-1 overflow-y-auto text-sm">
          {todos.map((todo) => (
            <li key={todo.engagementId} className="flex min-w-0 gap-2">
              {todo.shortId && <span className="shrink-0 font-mono text-xs leading-5 text-muted-foreground">{todo.shortId}</span>}
              <Link to={engagementDetailPath(todo.engagementId)} className="min-w-0 truncate hover:text-primary">
                {todo.title}
              </Link>
            </li>
          ))}
          {hiddenCount > 0 && <li className="text-muted-foreground">另有 {hiddenCount} 条你无权查看</li>}
        </ul>
        <AlertDialogFooter>
          <AlertDialogCancel>取消</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>仍然完成</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
);

export const useMyCompletion = (id: string, prdId?: string, issueType?: IssueType) => {
  const { toast } = useToast();
  const { mutate } = useSWRConfig();
  const [openTodos, setOpenTodos] = useState<OpenTodos | null>(null);
  const viewerId = useAuthStore((state) => state.auth?.user?.id ?? state.auth?.id);
  // One status per person (WED-192): Open / In Process / Done, freely switchable.
  const updateCompletion = (completedAt: string | null, startedAt: string | null) => {
    if (!viewerId) return;
    const status: PersonalStatus = completedAt ? "done" : startedAt ? "in_progress" : "open";
    const updateParticipants = (participants: Engagement["completionParticipants"]) =>
      participants?.map((participant) =>
        participant.userId === viewerId ? { ...participant, status, completedAt, startedAt } : participant
      );
    const updateEngagement = (engagement: Engagement): Engagement => {
      const completedMemberIds = new Set(engagement.completedMemberIds ?? []);
      if (engagement.memberIds?.includes(viewerId)) {
        if (completedAt) completedMemberIds.add(viewerId);
        else completedMemberIds.delete(viewerId);
      }
      return {
        ...engagement,
        myCompletedAt: completedAt,
        myStartedAt: startedAt,
        completedMemberIds: [...completedMemberIds],
        completionParticipants: updateParticipants(engagement.completionParticipants),
      };
    };
    mutate<Engagement>(
      (key) => Array.isArray(key) && key[0] === `/engagements/${id}`,
      (engagement) => engagement ? updateEngagement(engagement) : engagement,
      { revalidate: false },
    );
    mutate<Engagement[]>(
      (key) => Array.isArray(key) && key[0] === "/engagements",
      (engagements) => engagements?.map((engagement) =>
        engagement.id === id ? updateEngagement(engagement) : engagement
      ),
      { revalidate: false },
    );
    if (prdId) {
      mutate<Prd>(
        (key) => Array.isArray(key) && (key[0] === `/prds/${prdId}` || key[0] === `/prds/${id}`),
        (prd) => prd ? {
          ...prd,
          ...(prd.id === id ? {
            myCompletedAt: completedAt,
            myStartedAt: startedAt,
            completionParticipants: updateParticipants(prd.completionParticipants),
          } : {}),
          engagements: prd.engagements?.map((engagement) =>
            engagement.id === id ? updateEngagement(engagement) : engagement
          ),
          // Child rows (todo checkboxes on the parent page) read their own
          // engagement off the parent detail, so patch them here too.
          children: prd.children?.map((child) =>
            child.engagementId === id ? {
              ...child,
              engagements: child.engagements?.map((engagement) =>
                engagement.id === id ? updateEngagement(engagement) : engagement
              ),
            } : child
          ),
        } : prd,
        { revalidate: false },
      );
    }
  };
  // Exact keys only (WED-249), not every /prds* /engagements* key by prefix.
  // Success: lists already carry the optimistic result above, so just the
  // detail this page may have mounted and the inbox (a live queue) reload.
  // Failure: the optimistic list writes have to be rolled back too.
  const refreshCompletionData = (rollback = false) => {
    const paths = [`/prds/${id}`, prdId && `/prds/${prdId}`, INBOX_KEY, INBOX_COUNT_KEY];
    if (rollback) paths.push("/prds", "/engagements", `/engagements/${id}`);
    return mutate((key) => Array.isArray(key) && paths.includes(key[0]));
  };
  const options = {
    onSuccess: (result: CompletionResult) => {
      updateCompletion(result.myCompletedAt, result.myStartedAt ?? null);
      refreshCompletionData();
    },
    onError: (error: unknown) => {
      refreshCompletionData(true);
      const todos = openTodosOf(error);
      if (todos) setOpenTodos(todos);
      else toastError(toast, "更新个人进度失败", error);
    },
  };
  const completion = useSWRMutation(`/prds/${id}`, putFetcher<CompletionResult>, options);
  const reminder = useSWRMutation(`/prds/${id}/remind`, postFetcher<{ remindedCount: number }>, {
    onSuccess: (result) => {
      refreshCompletionData();
      toast({ description: result.remindedCount ? `已发起催办（${result.remindedCount} 人）` : "成员均已完成" });
    },
    onError: (error: unknown) => toastError(toast, "催办失败", error),
  });
  const setMyStatus = (myStatus: PersonalStatus, ignoreOpenTodos = false) => {
    // A bug may be refused with 409 OPEN_TODOS: wait for the server instead of
    // flashing Done and rolling back (onSuccess applies the result).
    if (myStatus !== "done" || ignoreOpenTodos || issueType !== "bug") {
      const now = new Date().toISOString();
      updateCompletion(myStatus === "done" ? now : null, myStatus === "in_progress" ? now : null);
    }
    return completion.trigger(ignoreOpenTodos ? { myStatus, ignoreOpenTodos } : { myStatus }).catch(() => {});
  };
  // Callers render this next to their Done control.
  const openTodosDialog: ReactNode = openTodos && (
    <OpenTodosDialog
      {...openTodos}
      onCancel={() => setOpenTodos(null)}
      onConfirm={() => {
        setOpenTodos(null);
        setMyStatus("done", true);
      }}
    />
  );
  return {
    setMyStatus,
    openTodosDialog,
    remindMembers: () => reminder.trigger({}).catch(() => {}),
    isMutating: completion.isMutating || reminder.isMutating,
  };
};

export const RemindMembersButton = ({ id, primary = false }: { id: string; primary?: boolean }) => {
  const { remindMembers, isMutating } = useMyCompletion(id);
  return (
    <Button size="sm" variant={primary ? "default" : "outline"} disabled={isMutating}
      title="催办尚未完成的执行成员"
      onClick={(event) => { event.stopPropagation(); remindMembers(); }}>
      <BellRingIcon aria-hidden="true" className="h-4 w-4" />
      催办
    </Button>
  );
};

const STATUS_OPTIONS: { status: PersonalStatus; label: string; Icon: (typeof STATUS_ICON)[PersonalStatus]; className?: string }[] = [
  { status: "open", label: STATUS_LABEL.open, Icon: STATUS_ICON.open },
  { status: "in_progress", label: STATUS_LABEL.in_progress, Icon: STATUS_ICON.in_progress, className: "text-primary" },
  { status: "done", label: STATUS_LABEL.done, Icon: STATUS_ICON.done, className: "text-green-600" },
];

// Each member switches only their own status, freely among the three (WED-192).
// PRD / App owners additionally get 催办 while other members are unfinished;
// an App owner who is not a member only gets 催办.
// `remind={false}` (version list rows): status only, 催办 stays on the PRD page.
export const MyEngagementStatus = ({ engagement: e, prdId, issueType = e.prd?.type, remind = true, size = "sm" }: {
  engagement: Engagement;
  prdId?: string;
  issueType?: IssueType;
  remind?: boolean;
  /** "xs" in list rows, next to the other xs row actions. */
  size?: "sm" | "xs";
}) => {
  const { setMyStatus, openTodosDialog, isMutating } = useMyCompletion(e.id, prdId, issueType);
  const canRemind = remind && e.canRemind;
  if (!e.canComplete && !canRemind) return null;
  const current = STATUS_OPTIONS.find((option) => option.status === personalStatusOf(e))!;
  return (
    <div role="group" aria-label="My work status" className="flex items-center gap-2">
      {canRemind && <RemindMembersButton id={e.id} />}
      {e.canComplete && <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant={current.status === "open" ? "default" : "secondary"} size={size} disabled={isMutating} title="我的处理状态">
            <StatusIcon status={current.status} className={cn("h-4 w-4", current.className)} />
            <span className={current.className}>{current.label}</span>
            <ChevronDownIcon aria-hidden="true" className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {STATUS_OPTIONS.filter((option) => option !== current).map(({ status, label, Icon }) => (
            <DropdownMenuItem key={status} disabled={isMutating} onSelect={() => setMyStatus(status)}>
              <Icon aria-hidden="true" />
              {label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>}
      {openTodosDialog}
    </div>
  );
};

const MyWorkRow = ({ engagement: e, n, prd }: { engagement: Engagement; n: number; prd: Prd }) => {
  const app = useApp(e.appId);
  return <RowCard>
    <RowIndex n={n} />
    <span className="flex-1 text-sm">{app?.name ?? e.appId}</span>
    <MyEngagementStatus engagement={e} prdId={prd.id} issueType={prd.type} />
  </RowCard>;
};

export const MyEngagementWork = ({ prd, appId }: { prd: Prd; appId?: string }) => {
  const rows = (prd.engagements ?? []).filter(e => (e.canComplete || e.canRemind) && (!appId || e.appId === appId));
  if (!rows.length) return null;
  return <Section title="我的处理进度" className="mb-6">
    <div className="flex flex-col gap-1">
    {rows.map((e, index) => <MyWorkRow key={e.id} engagement={e} n={index + 1} prd={prd} />)}
    </div>
  </Section>;
};
